import {
  ControlledInput,
  ControlledMultiSelect,
  emailInputPattern,
  ExtranetContainer,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacButton,
  PharmacContainer,
  PharmacFieldset,
  PharmacFieldsetLegend,
  PharmacH5,
  PharmacIcon,
  PharmacLabel,
  PharmacText,
  PharmacTooltip,
} from '@pharmac/ui'
import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { api } from '../../services/HttpService'

type Props = {
  cancelButtonClick?: () => void
  saveButtonClick?: () => void
  saveDisabled?: boolean
}

export const UpdateUserInfoForm = ({ cancelButtonClick, saveButtonClick, saveDisabled }: Props) => {
  const { getChangePasswordUrl } = useAuthenticationContext()
  const { t, i18n, key } = useLanguage(['extranet', 'common'])
  const { control } = useFormContext()
  const { minDesktop, maxMobile, minTablet } = useBreakpoint()

  const handleChangePassword = () => {
    getChangePasswordUrl(i18n.language as 'fi' | 'en' | 'sv').then((url) => {
      if (!!url) {
        window.location.replace(url)
      }
    })
  }

  const { data: professionalRoles } = useQuery({
    queryKey: ['__professionalRoles'],
    queryFn: async () => await api.customers.geUserProfessionalRoles(),
  })

  return (
    <>
      <PharmacFieldset style={maxMobile ? { padding: 0 } : undefined}>
        <PharmacFieldsetLegend marginSmallLeft={maxMobile}>
          <PharmacH5>{t('basicInfo', { ns: 'common' })}</PharmacH5>
        </PharmacFieldsetLegend>
        <ExtranetContainer
          full
          flexColumn
        >
          <ExtranetContainer
            paddingMediumLeft={minDesktop}
            paddingMediumTop={minTablet}
            paddingLargeTop={maxMobile}
            paddingMediumBottom
            full
            flexColumn={!minDesktop}
          >
            <ExtranetContainer
              paddingMediumHorizontal={maxMobile}
              half={minDesktop}
              full={!minDesktop}
              flexColumn
            >
              <PharmacContainer
                paddingMediumBottom
                style={minDesktop ? { width: '95%' } : { width: '100%' }}
              >
                <ControlledInput
                  name="lastName"
                  rules={{ required: true }}
                  requiredError={t('common:requiredFieldError')}
                  control={control}
                  label={t('lastName', { ns: 'common' })}
                />
              </PharmacContainer>

              <PharmacContainer
                paddingMediumBottom
                style={minDesktop ? { width: '95%' } : { width: '100%' }}
              >
                <ControlledInput
                  name="firstName"
                  rules={{ required: true }}
                  requiredError={t('common:requiredFieldError')}
                  control={control}
                  label={t('firstName', { ns: 'common' })}
                />
              </PharmacContainer>
              <PharmacContainer
                paddingMediumBottom
                style={minDesktop ? { width: '95%' } : { width: '100%' }}
              >
                <ControlledInput
                  name="emailAddress"
                  rules={{ required: true, pattern: emailInputPattern }}
                  requiredError={t('common:requiredFieldError')}
                  patternError={t('common:emailPatternError')}
                  control={control}
                  label={t('emailAddress', { ns: 'common' })}
                />
              </PharmacContainer>
              <PharmacContainer
                paddingMediumBottom
                style={minDesktop ? { width: '95%' } : { width: '100%' }}
              >
                <ControlledInput
                  name="phoneNumber"
                  control={control}
                  label={t('phoneNumber', { ns: 'common' })}
                />
              </PharmacContainer>
              {minDesktop && (
                <PharmacContainer
                  paddingMediumBottom
                  style={{ width: '95%' }}
                >
                  <ControlledInput
                    name="certificateCardNumber"
                    control={control}
                    label={t('certificateCardNumber', { ns: 'common' })}
                  />
                </PharmacContainer>
              )}
            </ExtranetContainer>

            <ExtranetContainer
              half={minDesktop}
              full={!minDesktop}
              paddingMediumHorizontal={maxMobile}
              flexColumn
            >
              <PharmacContainer
                paddingMediumBottom
                style={minDesktop ? { width: '95%' } : { width: '100%' }}
              >
                <ControlledInput
                  name="streetName"
                  control={control}
                  label={t('streetName', { ns: 'common' })}
                />
              </PharmacContainer>
              <PharmacContainer
                paddingMediumBottom
                style={minDesktop ? { width: '95%' } : { width: '100%' }}
              >
                <ControlledInput
                  name="postCode"
                  control={control}
                  label={t('postCode', { ns: 'common' })}
                />
              </PharmacContainer>
              <PharmacContainer
                paddingMediumBottom
                style={minDesktop ? { width: '95%' } : { width: '100%' }}
              >
                <ControlledInput
                  name="townName"
                  control={control}
                  label={t('townName', { ns: 'common' })}
                />
              </PharmacContainer>
              <PharmacContainer
                paddingMediumBottom
                style={minDesktop ? { width: '95%' } : { width: '100%' }}
              >
                <ExtranetContainer
                  style={{ width: 'fit-content' }}
                  flexRow
                  alignCenter
                  paddingSmallBottom
                >
                  <PharmacLabel>{t('common:countryCode')}*</PharmacLabel>
                  <PharmacTooltip
                    paddingSmallLeft
                    handle={
                      <PharmacIcon
                        style={{ color: 'var(--color-info)' }}
                        size="18px"
                        icon="info"
                      />
                    }
                  >
                    <PharmacText>
                      {t('extranet:inviteLinks.registration.countryTooltip')}
                    </PharmacText>
                  </PharmacTooltip>
                </ExtranetContainer>
                <ControlledInput
                  rules={{ required: true }}
                  requiredError={t('common:requiredFieldError')}
                  name="countryCode"
                  control={control}
                />
              </PharmacContainer>
              {!minDesktop && (
                <PharmacContainer
                  paddingMediumBottom
                  style={{ width: '100%' }}
                >
                  <ControlledInput
                    name="certificateCardNumber"
                    control={control}
                    label={t('certificateCardNumber', { ns: 'common' })}
                  />
                </PharmacContainer>
              )}
              <PharmacContainer
                paddingMediumBottom
                style={minDesktop ? { width: '95%' } : { width: '100%' }}
              >
                <ExtranetContainer
                  style={{ width: 'fit-content' }}
                  flexRow
                  alignCenter
                  paddingSmallBottom
                >
                  <PharmacLabel>{t('common:professionalRoles')}</PharmacLabel>
                  <PharmacTooltip
                    paddingSmallLeft
                    handle={
                      <PharmacIcon
                        style={{ color: 'var(--color-info)' }}
                        size="18px"
                        icon="info"
                      />
                    }
                  >
                    <PharmacText>
                      {t('extranet:inviteLinks.registration.selectProfessionalRoleInfo')}
                    </PharmacText>
                  </PharmacTooltip>
                </ExtranetContainer>
                <ControlledMultiSelect
                  control={control}
                  name="professionalRoleIds"
                >
                  {professionalRoles?.map((a) => (
                    <option
                      key={a.id}
                      value={a.id}
                    >
                      {key(a.displayName)}
                    </option>
                  ))}
                </ControlledMultiSelect>
              </PharmacContainer>
            </ExtranetContainer>
          </ExtranetContainer>
          <ExtranetContainer
            style={{ width: '95%' }}
            paddingLargeVertical
            justifyCenter
          >
            <PharmacButton
              fullWidth
              onClick={handleChangePassword}
            >
              {t('changePassword', { ns: 'common' })}
            </PharmacButton>
          </ExtranetContainer>
        </ExtranetContainer>
      </PharmacFieldset>
      <ExtranetContainer
        paddingLargeTop
        full
        justifyEnd={minDesktop}
        justifyCenter={!minDesktop}
      >
        <PharmacButton
          fullWidth={!minDesktop}
          variant="secondary"
          marginLargeRight
          onClick={cancelButtonClick}
        >
          {t('cancel', { ns: 'common' })}
        </PharmacButton>
        <PharmacButton
          fullWidth={!minDesktop}
          onClick={saveButtonClick}
          disabled={saveDisabled}
        >
          {t('save', { ns: 'common' })}
        </PharmacButton>
      </ExtranetContainer>
    </>
  )
}
